const STORAGE_QUEUE_KEY = "analytics_queue_v1";
const ID_LINK_KEY = "identity_linked";
const RETRY_LIMIT = 3;
const PII_KEYS = ["password", "token", "authorization", "cookie", "ssn", "credit_card", "jwt", "secret", "email", "phone"];

let analyticsConfig = {
  appId: "",
  env: "",
  collectorUrl: "",
};
let identifiedUserId = null;
let initialized = false;

function isPrerender() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return ua.includes("ReactSnap") || ua.includes("HeadlessChrome");
}

function parseCookies() {
  if (typeof document === "undefined") return {};
  return document.cookie.split("; ").reduce((acc, entry) => {
    const [k, v] = entry.split("=");
    if (k) acc[k] = decodeURIComponent(v || "");
    return acc;
  }, {});
}

function readCookie(name) {
  const cookies = parseCookies();
  return cookies[name];
}

function ensureId(cookieName, days) {
  let id = readCookie(cookieName);
  if (id) return id;
  const next = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${cookieName}=${next}; Path=/; Secure; SameSite=Lax; Expires=${expires}`;
  return next;
}

function getAttrib() {
  const raw = readCookie("pv_attrib");
  if (!raw) return {};
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return {};
  }
}

function sanitizeProps(props = {}) {
  const clean = {};
  Object.entries(props || {}).forEach(([k, v]) => {
    if (PII_KEYS.includes(String(k).toLowerCase())) return;
    clean[k] = v;
  });
  return clean;
}

function queueEvent(evt) {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_QUEUE_KEY) || "[]");
    existing.push(evt);
    localStorage.setItem(STORAGE_QUEUE_KEY, JSON.stringify(existing));
  } catch {
    // ignore
  }
}

function flushQueue(sender) {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_QUEUE_KEY) || "[]");
    if (!existing.length) return;
    localStorage.removeItem(STORAGE_QUEUE_KEY);
    existing.forEach((evt) => sender(evt, 0));
  } catch {
    // ignore
  }
}

function send(evt, attempt = 0) {
  if (!analyticsConfig.collectorUrl || isPrerender()) {
    if (analyticsConfig.env === "dev" && !isPrerender()) {
      // No analytics collector is configured in this build; keep production quiet.
      // eslint-disable-next-line no-console
      console.debug("[analytics:no-op]", evt.event_name, evt.props || {});
    }
    return;
  }
  const body = JSON.stringify(evt);
  const sender = () => {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(analyticsConfig.collectorUrl, blob);
      if (ok) return Promise.resolve();
    }
    return fetch(analyticsConfig.collectorUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      mode: "cors",
    });
  };

  sender().catch(() => {
    if (attempt + 1 >= RETRY_LIMIT) {
      queueEvent(evt);
      return;
    }
    const backoff = Math.pow(2, attempt) * 300;
    setTimeout(() => send(evt, attempt + 1), backoff);
  });
}

export function initAnalytics({ appId, env, collectorUrl }) {
  analyticsConfig = { appId, env, collectorUrl };
  initialized = true;
  if (typeof window !== "undefined") {
    flushQueue(send);
  }
}

export function identifyUser(userId) {
  identifiedUserId = userId;
  const anonId = readCookie("pv_anon") || "";
  if (!userId || !anonId) return;
  const key = `${ID_LINK_KEY}:${userId}:${anonId}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    track("IdentityLinked", { anon_id: anonId, user_id: userId });
  } catch {
    // ignore
  }
}

export function track(eventName, props = {}) {
  if (!initialized || typeof window === "undefined") return;
  const cookies = parseCookies();
  const anonId = cookies["pv_anon"] || ensureId("pv_anon", 365);
  const sessionId = cookies["pv_sess"] || ensureId("pv_sess", 1);
  const attrib = getAttrib();
  const payload = {
    app_id: analyticsConfig.appId,
    env: analyticsConfig.env,
    event_name: eventName,
    ts: new Date().toISOString(),
    anon_id: anonId,
    user_id: identifiedUserId || undefined,
    session_id: sessionId,
    page_path: window.location?.pathname,
    referrer: document.referrer || attrib.ref || undefined,
    utm_source: attrib.utm_source,
    utm_medium: attrib.utm_medium,
    utm_campaign: attrib.utm_campaign,
    utm_content: attrib.utm_content,
    utm_term: attrib.utm_term,
    props: sanitizeProps(props),
  };
  send(payload, 0);
}

export function trackPageView() {
  track("LandingViewed");
}

export const EventNames = {
  LandingViewed: "LandingViewed",
  CtaClicked: "CtaClicked",
  ScanStarted: "scan_started",
  ScanCompleted: "scan_completed",
  AssessmentCtaClicked: "assessment_cta_clicked",
  AssessmentRequestStarted: "assessment_request_started",
  AssessmentRequestSubmitted: "assessment_request_submitted",
  PricingViewed: "PricingViewed",
  SignupStarted: "SignupStarted",
  SignupCompleted: "SignupCompleted",
  LoginCompleted: "LoginCompleted",
  Activation: "Activation",
  PaywallShown: "PaywallShown",
  CheckoutStarted: "CheckoutStarted",
  PurchaseCompleted: "PurchaseCompleted",
  FeatureUsed: "FeatureUsed",
  ErrorShown: "ErrorShown",
  WaitlistViewed: "WaitlistViewed",
  WaitlistJoined: "WaitlistJoined",
  ReferralCopied: "ReferralCopied",
  CreditsGranted: "CreditsGranted",
  CreditsUsed: "CreditsUsed",
  CreditsDepleted: "CreditsDepleted",
  TrialStarted: "TrialStarted",
  TrialExpired: "TrialExpired",
  ReferralVisited: "ReferralVisited",
  ReferralSignupCompleted: "ReferralSignupCompleted",
  ReferralRewardGranted: "ReferralRewardGranted",
};

export function getAnonId() {
  try {
    return readCookie("pv_anon") || ensureId("pv_anon", 365);
  } catch {
    return "anon";
  }
}

export const trackEvent = track;
export const trackLandingViewed = trackPageView;

